/**
 * Доля хвоста публичного рендерера: виды `weight_picker` и `height_picker` (вес и рост с выбором единицы).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с общим полем `UnitPickerBlock`. Метка `case 'weight_picker'` и `case 'height_picker'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderWeightPicker(ctx)` и `renderHeightPicker(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React, { useEffect, useRef, useState } from 'react';
// WEB-925 (гэп 1): ключ ответа единицы — один на рендер и на сборщик
// аналитики, чтобы ответы не разъехались (шрам WEB-923).
import {
  pickerUnitValueKey,
} from '@/components/quiz-runtime/answerCollection';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { aiFields } from '../aiShapedFields';
// stylepack/picker-input-look: вид поля ввода — та же дверь, что у number_input.
import {
  inputBoxStyleOverride,
  inputLabelStyleOverride,
  inputLookClassName,
  inputTextColorOverride,
  inputTextFontSizeOverride,
  type InputFieldStyleFields,
} from '../inputFieldStyle';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';
// Задание stylepack/unit-picker-look: вид «крупная цифра» и «пилюля».
import {
  feetInchesOf,
  feetInchesToValue,
  pickerUnits,
  resolveUnitPickerLook,
  unitSuffixFontSize,
  // Задание stylepack/unit-toggle-case: регистр и кегль подписи единиц.
  unitToggleLabelStyle,
  type ResolvedUnitPickerLook,
} from '../unitPickerLook';


interface UnitPickerBlockProps {
  testidPrefix: 'weight-picker' | 'height-picker';
  /** WEB-672 2a: pre-rendered rich/plain question node (undefined = no label). */
  question?: React.ReactNode;
  /**
   * Дозаказ владельца продукта: настоящий px-размер вопроса. Absent — без
   * явного inline fontSize (наследует), как и раньше.
   */
  questionFontSize?: number;
  unitDefault: string;
  /**
   * Задание stylepack/unit-set: единицы выбора из двери `pickerUnits` — две
   * (как раньше) или одна: тогда переключатель — одна плашка без выбора.
   */
  units: readonly [string] | readonly [string, string];
  /**
   * WEB-785 (возврат QA 01.08): подписи кнопок единиц. Раньше рисовался сам
   * КЛЮЧ единицы под `textTransform: uppercase` — на русской воронке торчали
   * «KG»/«LB». Подпись приходит из словаря по языку воронки; на неизвестной
   * единице фолбэк прежний (ключ в верхнем регистре).
   */
  unitLabels: Readonly<Record<string, string>>;
  min?: number;
  max?: number;
  blockId: string;
  primaryColor: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * WEB-1301: посетитель переключил единицу (kg/lb, cm/ft) — записывается
   * ОТДЕЛЬНО от value (см. pickerUnitValueKey в answerCollection.ts), чтобы
   * "70 kg" не попало в values[block.id] и не сломало Number()-сравнения
   * logic-engine. Опционален: без него блок ведёт себя как раньше.
   */
  onUnitChange?: (unit: string) => void;
  /**
   * Решение владельца №5: единица, которую посетитель уже выбрал (служебный
   * ключ `<id>::unit`). Без неё после возврата на экран переключатель
   * показывал единицу по умолчанию, а в ответе лежала выбранная — число и
   * единица расходились, и ИМТ считался бы не от того. Нет — как раньше.
   */
  storedUnit?: string;
  /**
   * Решение владельца №5: роль пикера веса (`weightPickerRole`) — в разметке
   * `data-role`, чтобы было видно, какой вес текущий, а какой желаемый.
   * Нет поля — нет атрибута (разметка старых воронок прежняя).
   */
  dataRole?: string;
  /** stylepack/picker-input-look: поля вида поля ввода (`input*`); пусто — разметка прежняя. */
  inputLook?: InputFieldStyleFields;
  /**
   * Задание stylepack/unit-picker-look: разобранный вид значения и
   * переключателя. Прежний вид (поле, кнопки справа) — прежняя разметка.
   */
  look: ResolvedUnitPickerLook;
}

/** Цвет крупной цифры и подписи единицы, когда автор его не задал. */
const BIG_NUMBER_DEFAULT_COLOR =
  'var(--color-text-on-surface, var(--color-text-primary, inherit))';
/** Цвет черты, когда автор его не задал, — акцент. */
const UNDERLINE_DEFAULT_COLOR = 'var(--color-primary, #4094F7)';

/** Подпись единицы у крупной цифры и в пилюле — строчными, как в данных. */
const lowerLabel = (
  unitLabels: Readonly<Record<string, string>>,
  unit: string,
): string => (unitLabels[unit] ?? unit).toLocaleLowerCase();

interface BigNumberFieldProps {
  testid: string;
  suffixTestid: string;
  suffix: string;
  value: string;
  onChange: (value: string) => void;
  look: ResolvedUnitPickerLook;
  widthCh: number;
  min?: number;
  max?: number;
}

/** Одна крупная цифра без рамки и мелкая подпись единицы справа. */
function BigNumberField({
  testid,
  suffixTestid,
  suffix,
  value,
  onChange,
  look,
  widthCh,
  min,
  max,
}: BigNumberFieldProps): React.ReactElement {
  const color = look.valueColor ?? BIG_NUMBER_DEFAULT_COLOR;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <input
        type="number"
        inputMode="decimal"
        placeholder="0"
        min={min}
        max={max}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        data-testid={testid}
        style={{
          borderStyle: 'none',
          outline: 'none',
          background: 'transparent',
          padding: 0,
          width: `${widthCh}ch`,
          minWidth: 0,
          textAlign: 'center',
          fontSize: look.valueFontSize,
          fontWeight: 700,
          lineHeight: 1.1,
          color,
          MozAppearance: 'textfield',
        }}
      />
      <span
        data-testid={suffixTestid}
        style={{
          fontSize: unitSuffixFontSize(look.valueFontSize),
          fontWeight: 600,
          color,
        }}
      >
        {suffix}
      </span>
    </span>
  );
}

interface FeetInchesProps {
  testidPrefix: string;
  unitLabels: Readonly<Record<string, string>>;
  value: string;
  onChange: (value: string) => void;
  look: ResolvedUnitPickerLook;
  min?: number;
  max?: number;
}

/**
 * Рост в футах: два поля «ft» и «in» в ряд. Ответ — ПРЕЖНИЙ (число в футах,
 * `feetInchesToValue`), поэтому пара живёт в своём состоянии: «5» и пустые
 * дюймы не должны превращаться обратно в «5» и «0» при каждом нажатии.
 */
function FeetInchesFields({
  testidPrefix,
  unitLabels,
  value,
  onChange,
  look,
  min,
  max,
}: FeetInchesProps): React.ReactElement {
  const [pair, setPair] = useState(() => feetInchesOf(value));
  const update = (next: { ft: string; inch: string }) => {
    setPair(next);
    onChange(feetInchesToValue(next.ft, next.inch));
  };
  return (
    <>
      <BigNumberField
        testid={`${testidPrefix}-input-ft`}
        suffixTestid={`${testidPrefix}-unit-suffix-ft`}
        suffix={lowerLabel(unitLabels, 'ft')}
        value={pair.ft}
        onChange={(ft) => update({ ...pair, ft })}
        look={look}
        widthCh={2}
        min={min}
        max={max}
      />
      <BigNumberField
        testid={`${testidPrefix}-input-in`}
        suffixTestid={`${testidPrefix}-unit-suffix-in`}
        suffix={lowerLabel(unitLabels, 'in')}
        value={pair.inch}
        onChange={(inch) => update({ ...pair, inch })}
        look={look}
        widthCh={2}
        min={0}
        max={11}
      />
    </>
  );
}

function UnitPickerBlock({
  testidPrefix,
  question,
  questionFontSize,
  unitDefault,
  units,
  unitLabels,
  min,
  max,
  value,
  onChange,
  onUnitChange,
  primaryColor,
  storedUnit,
  dataRole,
  look,
  inputLook = {},
}: UnitPickerBlockProps): React.ReactElement {
  const [picked, setUnit] = useState(
    storedUnit && units.includes(storedUnit) ? storedUnit : unitDefault || units[0],
  );
  // Задание stylepack/unit-set: показывается только единица из набора —
  // одна дверь и для противоречащего unitDefault, и для сохранённой чужой
  // единицы, и для набора, суженного автором на холсте после выбора в превью.
  const unit = units.includes(picked) ? picked : units[0];
  const single = units.length === 1;
  // Сброс — только на НОВОЕ умолчание от панели, не на монтирование: иначе
  // выбранная посетителем единица (storedUnit) терялась бы сразу.
  const lastUnitDefault = useRef(unitDefault);
  // WEB-676/WEB-677: the panel writes a new unitDefault while this block is
  // already mounted in the phone preview — sync local state so the "active"
  // unit button reflects the latest default. A user's own manual KG/LB
  // toggle in the preview stays sticky until the NEXT unitDefault change.
  useEffect(() => {
    if (lastUnitDefault.current === unitDefault) return;
    lastUnitDefault.current = unitDefault;
    setUnit(unitDefault || units[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitDefault]);
  const pick = (u: string) => {
    setUnit(u);
    onUnitChange?.(u);
  };
  // Прежнее поле ввода в рамке; `extra` — только для нового места
  // переключателя (над/под), прежний вид зовёт без него — разметка та же.
  const fieldInput = (extra?: React.CSSProperties) => (
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value)
      }
      data-testid={`${testidPrefix}-input`}
      className={inputLookClassName(inputLook)}
      style={{
        flex: 1,
        padding: '10px 14px',
        border: '2px solid var(--color-border-primary, #d9d9d9)',
        borderRadius: 'var(--border-radius-md, 8px)',
        fontSize: 16,
        boxSizing: 'border-box',
        background: 'var(--color-bg-surface, #fff)',
        color:
          'var(--color-text-on-surface, var(--color-text-primary, inherit))',
        ...inputBoxStyleOverride(inputLook),
        ...inputTextFontSizeOverride(inputLook),
        ...inputTextColorOverride(inputLook),
        ...extra,
      }}
    />
  );
  // Задание stylepack/unit-set: одна единица — та же выбранная кнопка, но
  // плашкой без нажатия (не button: выбирать нечего).
  const unitButtons = single ? (
    <span
      data-testid={`${testidPrefix}-unit-${unit}`}
      style={{
        padding: '10px 16px',
        borderRadius: 'var(--border-radius-md, 8px)',
        border: '2px solid var(--color-primary, #4094F7)',
        background: 'var(--color-primary-bg, #e6f4ff)',
        color:
          'var(--color-text-on-surface, var(--color-text-primary, inherit))',
        ...unitToggleLabelStyle(look),
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {unitLabels[unit] ?? unit.toUpperCase()}
    </span>
  ) : units.map((u) => (
    <button
      key={u}
      type="button"
      data-testid={`${testidPrefix}-unit-${u}`}
      onClick={() => pick(u)}
      aria-pressed={unit === u}
      style={{
        padding: '10px 16px',
        borderRadius: 'var(--border-radius-md, 8px)',
        border: `2px solid ${unit === u ? 'var(--color-primary, #4094F7)' : 'var(--color-border-primary, #d9d9d9)'}`,
        background:
          unit === u
            ? 'var(--color-primary-bg, #e6f4ff)'
            : 'var(--color-bg-surface, #fff)',
        // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
        color:
          'var(--color-text-on-surface, var(--color-text-primary, inherit))',
        ...unitToggleLabelStyle(look),
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {unitLabels[u] ?? u.toUpperCase()}
    </button>
  ));
  const legacy =
    look.look === 'field' &&
    look.toggleLook === 'buttons' &&
    look.togglePosition === 'right';
  const column = look.togglePosition !== 'right';

  // Задание stylepack/unit-picker-look: крупная цифра по центру без рамки,
  // у роста в футах — пара «ft» + «in»; черта — по желанию автора.
  const valueNode =
    look.look === 'big-number' ? (
      <div
        data-testid={`${testidPrefix}-value`}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'baseline',
          gap: 16,
          ...(column ? { width: '100%' } : { flex: 1 }),
          ...(look.underline
            ? {
                borderBottomWidth: 2,
                borderBottomStyle: 'solid' as const,
                borderBottomColor: look.underlineColor ?? UNDERLINE_DEFAULT_COLOR,
                paddingBottom: 4,
              }
            : {}),
        }}
      >
        {unit === 'ft' ? (
          <FeetInchesFields
            testidPrefix={testidPrefix}
            unitLabels={unitLabels}
            value={value}
            onChange={onChange}
            look={look}
            min={min}
            max={max}
          />
        ) : (
          <BigNumberField
            testid={`${testidPrefix}-input`}
            suffixTestid={`${testidPrefix}-unit-suffix`}
            suffix={lowerLabel(unitLabels, unit)}
            value={value}
            onChange={onChange}
            look={look}
            widthCh={4}
            min={min}
            max={max}
          />
        )}
      </div>
    ) : (
      fieldInput(column ? { flex: undefined, width: '100%' } : undefined)
    );

  // Пилюля: две половины на общей скруглённой подложке.
  const toggleNode =
    look.toggleLook === 'pill' ? (
      <div
        data-testid={`${testidPrefix}-unit-pill`}
        role={single ? undefined : 'group'}
        style={{
          display: 'inline-flex',
          flexShrink: 0,
          gap: 4,
          padding: 4,
          // shape-fixed: пилюля — форма переключателя, не тема (WEB-696).
          borderRadius: 999,
          background: look.toggleBg ?? 'var(--color-bg-secondary, #f0f0f0)',
        }}
      >
        {single ? (
          // Задание stylepack/unit-set: одна половина — цвета выбранной.
          <span
            data-testid={`${testidPrefix}-unit-${unit}`}
            style={{
              // shape-fixed: половина пилюли — та же форма.
              borderRadius: 999,
              padding: '8px 20px',
              minWidth: 64,
              boxSizing: 'border-box',
              textAlign: 'center',
              background:
                look.toggleActiveBg ?? 'var(--color-bg-surface, #fff)',
              color:
                look.toggleActiveText ?? 'var(--color-text-primary, inherit)',
              ...unitToggleLabelStyle(look),
              fontWeight: 600,
            }}
          >
            {lowerLabel(unitLabels, unit)}
          </span>
        ) : units.map((u) => (
          <button
            key={u}
            type="button"
            data-testid={`${testidPrefix}-unit-${u}`}
            onClick={() => pick(u)}
            aria-pressed={unit === u}
            style={{
              border: 'none',
              // shape-fixed: половина пилюли — та же форма.
              borderRadius: 999,
              padding: '8px 20px',
              minWidth: 64,
              background:
                unit === u
                  ? (look.toggleActiveBg ?? 'var(--color-bg-surface, #fff)')
                  : 'transparent',
              color:
                unit === u
                  ? (look.toggleActiveText ??
                    'var(--color-text-primary, inherit)')
                  : (look.toggleText ?? 'var(--color-text-secondary, inherit)'),
              ...unitToggleLabelStyle(look),
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {lowerLabel(unitLabels, u)}
          </button>
        ))}
      </div>
    ) : column ? (
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        {unitButtons}
      </div>
    ) : (
      unitButtons
    );

  return (
    <div data-testid={testidPrefix} data-role={dataRole} style={{ marginBottom: 16 }}>
      {question && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: 8,
            color: primaryColor,
            ...(questionFontSize ? { fontSize: questionFontSize } : {}),
            ...inputLabelStyleOverride(inputLook),
          }}
        >
          {question}
        </label>
      )}
      {legacy ? (
        <div style={{ display: 'flex', gap: 8 }}>
          {fieldInput()}
          {unitButtons}
        </div>
      ) : (
        <div
          data-testid={`${testidPrefix}-row`}
          style={
            column
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 16,
                }
              : { display: 'flex', gap: 8, alignItems: 'center' }
          }
        >
          {look.togglePosition === 'above' && toggleNode}
          {valueNode}
          {look.togglePosition !== 'above' && toggleNode}
        </div>
      )}
    </div>
  );
}

export function renderWeightPicker(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, primaryColor, richText, hasText, values, setValue } = ctx;
  const ai = aiFields(block);
  const question = block.content ?? ai.question;
  // Задание stylepack/unit-set: набор единиц; одна — её и показывает
  // UnitPickerBlock, противоречащее ей unitDefault не действует.
  const units = pickerUnits('weight_picker', block.unitOptions);
  const unitDefault =
    block.weightPickerUnitDefault ??
    (ai.unitDefault as 'kg' | 'lb') ??
    'kg';
  const min = block.weightPickerMin ?? ai.min;
  const max = block.weightPickerMax ?? ai.max;
  return (
    <UnitPickerBlock
      testidPrefix="weight-picker"
            dataRole={block.weightPickerRole}
      question={hasText(question) ? richText(question) : undefined}
      questionFontSize={block.fontSize}
      unitDefault={unitDefault}
      units={units}
      unitLabels={getPublicQuizStrings(varCtx?.system?.locale).unitLabels}
      min={min}
      max={max}
      blockId={block.id}
      primaryColor={primaryColor}
      value={values[block.id] ?? ''}
      onChange={(v) => setValue(block.id, v)}
      onUnitChange={(u) => setValue(pickerUnitValueKey(block.id), u)}
            storedUnit={values[pickerUnitValueKey(block.id)]}
      look={resolveUnitPickerLook(block)}
      inputLook={block}
    />
  );
}

export function renderHeightPicker(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, primaryColor, richText, hasText, values, setValue } = ctx;
  const ai = aiFields(block);
  const question = block.content ?? ai.question;
  // Задание stylepack/unit-set: набор единиц; одна — её и показывает
  // UnitPickerBlock, противоречащее ей unitDefault не действует.
  const units = pickerUnits('height_picker', block.unitOptions);
  const unitDefault =
    block.heightPickerUnitDefault ??
    (ai.unitDefault as 'cm' | 'ft') ??
    'cm';
  const min = block.heightPickerMin ?? ai.min;
  const max = block.heightPickerMax ?? ai.max;
  return (
    <UnitPickerBlock
      testidPrefix="height-picker"
      question={hasText(question) ? richText(question) : undefined}
      questionFontSize={block.fontSize}
      unitDefault={unitDefault}
      units={units}
      unitLabels={getPublicQuizStrings(varCtx?.system?.locale).unitLabels}
      min={min}
      max={max}
      blockId={block.id}
      primaryColor={primaryColor}
      value={values[block.id] ?? ''}
      onChange={(v) => setValue(block.id, v)}
      onUnitChange={(u) => setValue(pickerUnitValueKey(block.id), u)}
            storedUnit={values[pickerUnitValueKey(block.id)]}
      look={resolveUnitPickerLook(block)}
      inputLook={block}
    />
  );
}
